import { Box, Container, Typography, Divider } from '@mui/material'
import { NAVBAR_BG, BG_LIGHT } from '../theme'

// ── Shared types ──────────────────────────────────────────────────────────────

export type Block =
    | { type: 'para'; content: React.ReactNode }
    | { type: 'list'; items: React.ReactNode[] }
    | { type: 'table'; headers: string[]; rows: string[][] }

export type SubSection = {
    title: string
    blocks: Block[]
}

export type Section = {
    title: string
    blocks: Block[]
    subsections?: SubSection[]
}

export type PolicyContent = {
    title: string
    lastUpdated: string
    intro: Block[]
    sections: Section[]
}

// ── Block renderer ────────────────────────────────────────────────────────────

export function RenderBlock({ block }: { block: Block }) {
    if (block.type === 'para') {
        return (
            <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                {block.content}
            </Typography>
        )
    }

    if (block.type === 'list') {
        return (
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                {block.items.map((item, i) => (
                    <Box component="li" key={i} sx={{ mb: 0.75 }}>
                        <Typography variant="body1" sx={{ color: 'text.secondary' }}>{item}</Typography>
                    </Box>
                ))}
            </Box>
        )
    }

    if (block.type === 'table') {
        return (
            <Box sx={{ overflowX: 'auto', mb: 3 }}>
                <Box component="table" sx={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '14px',
                    '& th, & td': { border: '1px solid rgba(0,0,0,0.15)', p: 1.5, verticalAlign: 'top' },
                    '& th': { backgroundColor: NAVBAR_BG, color: 'white', fontWeight: 600 },
                    '& tr:nth-of-type(even)': { backgroundColor: 'rgba(0,0,0,0.03)' },
                }}>
                    <thead>
                        <tr>
                            {block.headers.map(h => <Box component="th" key={h}>{h}</Box>)}
                        </tr>
                    </thead>
                    <tbody>
                        {block.rows.map((row, i) => (
                            <tr key={i}>
                                {row.map((cell, j) => <td key={j}>{cell}</td>)}
                            </tr>
                        ))}
                    </tbody>
                </Box>
            </Box>
        )
    }

    return null
}

// ── Section renderers ─────────────────────────────────────────────────────────

export function RenderSubSection({ sub }: { sub: SubSection }) {
    return (
        <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                {sub.title}
            </Typography>
            {sub.blocks.map((block, i) => <RenderBlock key={i} block={block} />)}
        </Box>
    )
}

export function RenderSection({ section, index }: { section: Section; index: number }) {
    return (
        <>
            {index > 0 && <Divider sx={{ mb: 5 }} />}
            <Box sx={{ mb: 5 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
                    {section.title}
                </Typography>
                {section.blocks.map((block, i) => <RenderBlock key={i} block={block} />)}
                {section.subsections?.map((sub, i) => <RenderSubSection key={i} sub={sub} />)}
            </Box>
        </>
    )
}

// ── Page wrapper ──────────────────────────────────────────────────────────────

export function PolicyPage({ content }: { content: PolicyContent }) {
    const { title, lastUpdated, intro, sections } = content

    return (
        <Box>
            <Box sx={{ backgroundColor: NAVBAR_BG, pt: { xs: 10, md: 12 }, pb: { xs: 6, md: 8 } }}>
                <Container maxWidth="md">
                    <Typography variant="h2" sx={{ color: 'white', fontWeight: 800, mb: 2 }}>
                        {title}
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                        Last updated: {lastUpdated}
                    </Typography>
                </Container>
            </Box>

            <Box sx={{ backgroundColor: BG_LIGHT, py: { xs: 6, md: 8 } }}>
                <Container maxWidth="md">
                    <Box sx={{ mb: 5 }}>
                        {intro.map((block, i) => <RenderBlock key={i} block={block} />)}
                    </Box>
                    {sections.map((section, i) => (
                        <RenderSection key={i} section={section} index={i} />
                    ))}
                </Container>
            </Box>
        </Box>
    )
}
